import React, { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Loader2, LogOut, Mail, Phone, Trash2, Filter, CreditCard, FileText, Users, MessageSquare, Image as ImageIcon, Share2, FolderLock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BRANCHES } from "@/lib/brand";

const STATUSES = [
  { v: "new", label: "Nouveau" },
  { v: "in_progress", label: "En cours" },
  { v: "done", label: "Traité" },
  { v: "archived", label: "Archivé" },
];

function statusColor(s) {
  switch (s) {
    case "new": return "bg-white/10 text-white";
    case "in_progress": return "bg-amber-500/10 text-amber-400 border-amber-500/30";
    case "done": return "bg-emerald-500/10 text-emerald-400 border-emerald-500/30";
    case "archived": return "bg-zinc-500/10 text-zinc-400";
    default: return "bg-white/10 text-white";
  }
}

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterBranch, setFilterBranch] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selected, setSelected] = useState(null);

  const fetchAll = async () => {
    try {
      const [qs, st] = await Promise.all([
        api.get("/admin/quotes"),
        api.get("/admin/stats"),
      ]);
      setQuotes(qs.data);
      setStats(st.data);
    } catch (e) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (ready && user) fetchAll();
  }, [ready, user]);

  const filtered = useMemo(() => {
    return quotes.filter((q) => {
      if (filterBranch !== "all" && q.branch !== filterBranch) return false;
      if (filterStatus !== "all" && q.status !== filterStatus) return false;
      return true;
    });
  }, [quotes, filterBranch, filterStatus]);

  if (ready === false) return null;
  if (ready && !user) return <Navigate to="/admin/login" replace />;

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/quotes/${id}`, { status });
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      if (selected?.id === id) setSelected({ ...selected, status });
      toast.success("Statut mis à jour");
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const removeQuote = async (id) => {
    if (!window.confirm("Supprimer définitivement cette demande ?")) return;
    try {
      await api.delete(`/admin/quotes/${id}`);
      setQuotes((qs) => qs.filter((q) => q.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Demande supprimée");
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div className="pt-28 pb-24 min-h-screen" data-testid="page-admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Console</div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
              Tableau de bord
            </h1>
            <p className="mt-2 text-zinc-400 text-sm">
              Bienvenue {user?.name || user?.email}.
            </p>
          </div>
          <div className="flex gap-3">
            <Link
              to="/admin/messages"
              data-testid="admin-messages-link"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-white/15 text-sm hover:bg-white/5"
            >
              <MessageSquare size={14} /> Messages Gabriel
            </Link>
            <Link
              to="/admin/clients"
              data-testid="admin-clients-link"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-white/15 text-sm hover:bg-white/5"
            >
              <Users size={14} /> Base client
            </Link>
            <Link
              to="/admin/espace-client"
              data-testid="admin-portal-link"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-white/15 text-sm hover:bg-white/5"
            >
              <FolderLock size={14} /> Espace client
            </Link>
            <Link
              to="/admin/cartes-visite"
              data-testid="admin-cards-link"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-white/15 text-sm hover:bg-white/5"
            >
              <CreditCard size={14} /> Cartes de visite
            </Link>
            <Link
              to="/admin/depliant"
              data-testid="admin-brochure-link"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-white/15 text-sm hover:bg-white/5"
            >
              <FileText size={14} /> Dépliant 3 volets
            </Link>
            <Link
              to="/admin/bache"
              data-testid="admin-banner-link"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-white/15 text-sm hover:bg-white/5"
            >
              <ImageIcon size={14} /> Bâche façade
            </Link>
            <Link
              to="/admin/reseaux-sociaux"
              data-testid="admin-social-link"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-white/15 text-sm hover:bg-white/5"
            >
              <Share2 size={14} /> Réseaux sociaux
            </Link>
            <Link to="/" className="px-4 py-2 rounded-md border border-white/15 text-sm" data-testid="admin-go-site">
              Voir le site
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-white/15 text-sm hover:bg-white/5"
            >
              <LogOut size={14} /> Déconnexion
            </button>
          </div>
        </div>

        {/* STATS */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-10" data-testid="admin-stats">
          <StatCard label="Total demandes" value={stats?.total ?? "—"} accent="#ffffff" />
          <StatCard label="Nouvelles" value={stats?.new ?? "—"} accent="#ffffff" />
          <StatCard label="Studio" value={stats?.by_branch?.studio ?? 0} accent="#8b5cf6" />
          <StatCard label="Sécurité" value={stats?.by_branch?.securite ?? 0} accent="#ea580c" />
          <StatCard label="Propreté" value={stats?.by_branch?.proprete ?? 0} accent="#0ea5e9" />
        </div>

        {/* FILTERS */}
        <div className="mt-10 flex flex-wrap gap-3 items-center" data-testid="admin-filters">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500">
            <Filter size={14} /> Filtres
          </div>
          <select
            value={filterBranch}
            onChange={(e) => setFilterBranch(e.target.value)}
            data-testid="filter-branch"
            className="bg-[#141414] border border-white/10 rounded-md px-3 py-2 text-sm"
          >
            <option value="all">Tous les pôles</option>
            <option value="studio">Studio</option>
            <option value="securite">Sécurité</option>
            <option value="proprete">Propreté</option>
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            data-testid="filter-status"
            className="bg-[#141414] border border-white/10 rounded-md px-3 py-2 text-sm"
          >
            <option value="all">Tous les statuts</option>
            {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
          </select>
        </div>

        {/* TABLE */}
        <div className="mt-6 rounded-xl border border-white/10 overflow-hidden">
          {loading ? (
            <div className="p-16 flex items-center justify-center text-zinc-400">
              <Loader2 className="animate-spin mr-2" /> Chargement…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center text-zinc-500" data-testid="admin-empty">
              Aucune demande pour ces filtres.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-quotes-table">
                <thead className="bg-[#111] text-zinc-500 uppercase text-xs tracking-wider">
                  <tr>
                    <th className="text-left px-5 py-3">Date</th>
                    <th className="text-left px-5 py-3">Contact</th>
                    <th className="text-left px-5 py-3">Pôle</th>
                    <th className="text-left px-5 py-3">Service</th>
                    <th className="text-left px-5 py-3">Statut</th>
                    <th className="text-right px-5 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((q) => {
                    const br = BRANCHES[q.branch];
                    return (
                      <tr
                        key={q.id}
                        className="border-t border-white/5 hover:bg-white/[0.03] cursor-pointer"
                        onClick={() => setSelected(q)}
                        data-testid={`quote-row-${q.id}`}
                      >
                        <td className="px-5 py-4 text-zinc-400 whitespace-nowrap">
                          {new Date(q.created_at).toLocaleDateString("fr-FR", {
                            day: "2-digit", month: "2-digit", year: "2-digit",
                          })}
                        </td>
                        <td className="px-5 py-4">
                          <div className="font-medium">{q.name}</div>
                          <div className="text-xs text-zinc-500">{q.email}</div>
                        </td>
                        <td className="px-5 py-4">
                          <span
                            className="px-2 py-1 rounded-full text-xs font-medium border"
                            style={{
                              color: br?.accent,
                              borderColor: br?.accent + "55",
                              backgroundColor: br?.accent + "15",
                            }}
                          >
                            {br?.short}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-zinc-300 max-w-xs truncate">{q.service}</td>
                        <td className="px-5 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs border ${statusColor(q.status)}`}>
                            {STATUSES.find((s) => s.v === q.status)?.label || q.status}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeQuote(q.id);
                            }}
                            data-testid={`quote-delete-${q.id}`}
                            className="p-2 rounded hover:bg-white/10 text-zinc-400 hover:text-red-400"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* DETAIL DRAWER */}
      {selected && (
        <div className="fixed inset-0 z-50 flex" data-testid="admin-detail-drawer">
          <div className="flex-1 bg-black/70 backdrop-blur-sm" onClick={() => setSelected(null)} />
          <aside className="w-full sm:max-w-xl bg-[#0e0e0e] border-l border-white/10 overflow-y-auto">
            <div className="p-8">
              <button
                onClick={() => setSelected(null)}
                className="text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-white"
                data-testid="detail-close"
              >
                ← Fermer
              </button>
              <h3 className="font-display text-3xl font-semibold mt-4">{selected.name}</h3>
              <div className="mt-2 text-sm text-zinc-400 flex flex-wrap gap-4">
                <a href={`mailto:${selected.email}`} className="inline-flex items-center gap-2">
                  <Mail size={14} /> {selected.email}
                </a>
                {selected.phone && (
                  <a href={`tel:${selected.phone}`} className="inline-flex items-center gap-2">
                    <Phone size={14} /> {selected.phone}
                  </a>
                )}
              </div>

              <div className="mt-6 grid grid-cols-2 gap-4">
                <Info label="Pôle" value={BRANCHES[selected.branch]?.name} accent={BRANCHES[selected.branch]?.accent} />
                <Info label="Service" value={selected.service} />
                {selected.company && <Info label="Entreprise" value={selected.company} />}
                {selected.referral && <Info label="Comment connu ?" value={selected.referral} />}
                <Info label="Reçue le" value={new Date(selected.created_at).toLocaleString("fr-FR")} />
              </div>

              <div className="mt-6">
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Message</div>
                <div className="text-sm text-zinc-200 whitespace-pre-wrap rounded-md bg-[#141414] border border-white/10 p-4">
                  {selected.message}
                </div>
              </div>

              <div className="mt-8">
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Statut</div>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((s) => (
                    <button
                      key={s.v}
                      onClick={() => updateStatus(selected.id, s.v)}
                      data-testid={`detail-status-${s.v}`}
                      className={`px-3 py-1.5 rounded-md text-xs border ${
                        selected.status === s.v
                          ? "bg-white text-black border-white"
                          : "border-white/15 text-zinc-300 hover:bg-white/5"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#0e0e0e] p-5 relative overflow-hidden">
      <div
        className="absolute -bottom-10 -right-10 h-32 w-32 blur-3xl opacity-30 rounded-full"
        style={{ backgroundColor: accent }}
      />
      <div className="relative">
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{label}</div>
        <div className="font-display text-3xl font-bold mt-2" style={{ color: accent }}>{value}</div>
      </div>
    </div>
  );
}

function Info({ label, value, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{label}</div>
      <div className="mt-1 text-sm" style={accent ? { color: accent } : {}}>{value}</div>
    </div>
  );
}
